import { apiV2, errorHandler } from "./index";
import { markGeneralDataWrite, getGeneralDataWriteSeq } from "./generalDataSync";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  API_ERROR,
  FETCH_ALL_GENERAL_DATA,
  FETCH_ALL_CHALLENGES,
  FETCH_ONE_CHALLENGE,
} from "../constants/ActionTypes";


//= ===============================
// Session actions
//= ===============================

export function fetchAllChallenges() {
  return function (dispatch) {
    apiV2.get('/challenges')
      .then((response) => {
        const challenges = response.data.objects.reduce((acc, el) => {
          acc[el.challenge_id] = el;
          return acc;
        }, {});
        dispatch({
          type: FETCH_ALL_CHALLENGES,
          payload: challenges,
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function fetchChallengeIntro() {
  return function (dispatch) {
    const seq = getGeneralDataWriteSeq();
    apiV2.get('/challenges/intro')
      .then((response) => {
        // A save landed while this fetch was in flight — its payload is
        // stale, don't revert the just-written baseline.
        if (seq !== getGeneralDataWriteSeq()) return;
        dispatch({
          type: FETCH_ALL_GENERAL_DATA,
          payload: response.data,
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function updateChallengeIntro(challenge_intro_text, onSuccess) {
  return async function (dispatch, getState) {
    try {
      await apiV2.put('/general-info/challenge_intro_text', {
        general_info_value: challenge_intro_text,
      });
      // Sync the saved value into the general_data slice so consumers
      // using it as their baseline (InAppDescriptionPanel) see the save
      // reflected without a refetch.
      markGeneralDataWrite();
      dispatch({
        type: FETCH_ALL_GENERAL_DATA,
        payload: {
          ...getState().general_data?.general_data,
          challenge_intro_text,
        },
      });
      Swal.fire({
        icon: "success",
        title: "Saved!",
        timer: 1000,
      }).then(() => {
        onSuccess?.();
      });
    } catch (error) {
      errorHandler(dispatch, error.response, API_ERROR);
    }
  };
}

export function fetchOneChallenge(challenge_id) {
  return function (dispatch) {
    apiV2.get(`/challenges/${challenge_id}`)
      .then((response) => {
        const challenge = response.data;
        dispatch({
          type: FETCH_ONE_CHALLENGE,
          payload: { [String(challenge.challenge_id)]: challenge },
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function clearAllChallenges() {
  return function (dispatch) {
    dispatch({
      type: FETCH_ALL_CHALLENGES,
      payload: null,
    });
  };
}

export function addEditChallenges(options, onSuccess) {
  return function (dispatch) {
    // options.challenge_json is a JSON string from the component; parse it to get flat fields
    const challengeData = typeof options.challenge_json === 'string'
      ? JSON.parse(options.challenge_json)
      : options.challenge_json;

    // options.session_ids_json is a JSON string from the component; parse and transform
    const rawSessions = typeof options.session_ids_json === 'string'
      ? JSON.parse(options.session_ids_json)
      : options.session_ids_json;

    // Transform from legacy format [{ 902: ["Day 2", 1] }] to [{ session_id, challenge_day }]
    const challengeSessions = (rawSessions || []).map(entry => {
      const sessionId = Object.keys(entry)[0];
      const [challengeDay] = entry[sessionId];
      return { session_id: parseInt(sessionId), challenge_day: challengeDay };
    });

    // options.intro_session_json is a JSON string from the component; parse it
    const introSession = typeof options.intro_session_json === 'string'
      ? JSON.parse(options.intro_session_json)
      : options.intro_session_json;

    const {
      challenge_title,
      challenge_description,
      challenge_image,
      challenge_image_wide,
      challenge_is_active,
      challenge_day_type,
      challenge_is_new: is_new,
      challenge_author_id,
      total_days,
      challenge_is_dark_mode,
      challenge_certificate_image,
      challenge_invite_image,
      challenge_reminder_image,
      challenge_goal,
      challenge_is_free,
      original_challenge_is_free,
    } = challengeData;

    const payload = {
      challenge_title,
      challenge_description,
      challenge_image,
      challenge_image_wide,
      challenge_is_active,
      challenge_day_type,
      is_new: is_new ?? 0,
      challenge_author_id,
      total_days: parseInt(total_days) || 0,
      challenge_is_dark_mode,
      challenge_certificate_image,
      challenge_invite_image,
      challenge_reminder_image,
      challenge_goal,
      challenge_is_free,
      original_challenge_is_free,
      intro_session: {
        ...introSession,
        challenge_day: introSession.challenge_day || 'Intro',
      },
      challenge_sessions: challengeSessions,
    };

    const request = challengeData.challenge_id
      ? apiV2.put(`/challenges/${challengeData.challenge_id}`, payload)
      : apiV2.post('/challenges', payload);

    request
      .then((response) => {
        Swal.fire({
          icon: "success",
          title: "Saved!",
          timer: 1000,
        }).then(() => {
          onSuccess?.();
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function deleteChallenges(challenge_id, onSuccess) {
  return function (dispatch) {
    apiV2.delete(`/challenges/${challenge_id}`)
      .then((response) => {
        Swal.fire({
          icon: "success",
          title: "Challenge Deleted!",
          timer: 1000,
        }).then(() => {
          onSuccess?.();
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function sortChallenges(challenge_ids) {
  return function (dispatch) {
    const ids = typeof challenge_ids === 'string'
      ? JSON.parse(challenge_ids)
      : challenge_ids;

    apiV2.patch('/challenges/sort', {
      challenge_ids: ids,
    })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}
